import {
    createDataMapper,
    hasValues,
    isEmpty,
    isObject,
    mergeWithDefault,
} from '@common/utils';
import { FALLBACK_LOCALES, SupportedLocaleCode } from '@constants/locales';
import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import {
    BackendCollectionMetadata,
    BackendSaveOverrideResponse,
    CollectionMetadata,
    Credits,
    DEFAULT_CREDITS,
    DEFAULT_IMAGE,
    DEFAULT_MULTILINGUAL_METADATA,
    DEFAULT_SYNOPSIS,
    GetCollectionMetadataArgs,
    GetCollectionMetadataResponse,
    GetOverrideHistoryArgs,
    GetOverrideHistoryResponse,
    Image,
    LocalizedSynopses,
    MamutilsBaseSchema,
    MultilingualCollectionMetadata,
    MultilingualMetadataSource,
    SaveCollectionOverrideArgs,
    SaveCollectionOverrideResponse,
    Sources,
    Synopses,
} from '../types';

export const getLocalizedField = <T>(
    field: Record<SupportedLocaleCode, T> | undefined,
    locale: SupportedLocaleCode,
    fallback: T,
): T => {
    if (!field) return fallback;

    const searchOrder = [locale, FALLBACK_LOCALES[locale]].filter(Boolean);

    const validLocale = searchOrder.find((currentLocale) => {
        const value = field[currentLocale];

        if (isEmpty(value)) return false;
        if (isObject(value) && !hasValues(value as object)) return false;

        return true;
    });

    return validLocale ? field[validLocale] : fallback;
};

export const flattenMetadata = (
    fatMetadata: MultilingualCollectionMetadata,
    locale: SupportedLocaleCode,
): CollectionMetadata => {
    return {
        ...fatMetadata,
        title: getLocalizedField<string>(fatMetadata.title, locale, ''),
        genres: getLocalizedField<string[]>(fatMetadata.genres, locale, []),
        synopsis: getLocalizedField<Synopses>(
            fatMetadata.synopsis,
            locale,
            DEFAULT_SYNOPSIS,
        ),
    };
};

const mergeSources = (
    sources?: BackendCollectionMetadata['sources'],
): Partial<MamutilsBaseSchema> => {
    if (!sources) return {};

    return [
        sources.tmdb,
        sources.viva,
        sources.proscope,
        sources.user_overrides,
    ]
        .filter((source): source is MamutilsBaseSchema => Boolean(source))
        .reduce(
            (targetFallback, currentSource) =>
                mergeWithDefault(currentSource, targetFallback),
            {},
        );
};

const formatImage = (image?: Image | null) =>
    mergeWithDefault(image, DEFAULT_IMAGE);

const formatCredits = (credits?: Credits): Credits =>
    mergeWithDefault(credits, DEFAULT_CREDITS);

const mapToFrontendSchema = (
    source: Partial<MamutilsBaseSchema> | undefined,
    proscopeId: string,
    collectionId: string,
): MultilingualCollectionMetadata => {
    if (!source) {
        return mergeWithDefault(
            { proscopeId, collectionId },
            DEFAULT_MULTILINGUAL_METADATA,
        );
    }

    const mappedPartial: Partial<MultilingualCollectionMetadata> = {
        ...source,
        proscopeId,
        collectionId,
        releaseYear: source.releaseYear?.toString(),
        productionYear: source.productionYear?.toString(),
        synopsis: source.synopsis as LocalizedSynopses,
        credits: formatCredits(source.credits),
        images: {
            background: formatImage(source.images?.background),
            cover: formatImage(source.images?.cover),
            landscape: formatImage(source.images?.landscape),
        },
    };

    return mergeWithDefault(mappedPartial, DEFAULT_MULTILINGUAL_METADATA);
};

const mapBackendCollectionMetadataToFrontendCollectionMetadata = (
    backendMetadata: BackendCollectionMetadata,
    _meta: FetchBaseQueryMeta | undefined,
    arg: GetCollectionMetadataArgs,
): GetCollectionMetadataResponse => {
    const { proscopeId, collectionId } = arg;

    const rawMerged = mergeSources(backendMetadata.sources);

    const mappedSources = Object.entries(
        backendMetadata.sources,
    ).reduce<MultilingualMetadataSource>((acc, [sourceKey, sourceData]) => {
        acc[sourceKey as Sources] = mapToFrontendSchema(
            sourceData,
            proscopeId,
            collectionId,
        );
        return acc;
    }, {} as MultilingualMetadataSource);
    return {
        merged: mapToFrontendSchema(rawMerged, proscopeId, collectionId),
        sources: mappedSources,
    };
};

export const transformCollectionMetadata = createDataMapper<
    BackendCollectionMetadata,
    GetCollectionMetadataResponse,
    GetCollectionMetadataArgs
>(mapBackendCollectionMetadataToFrontendCollectionMetadata, false);

const mapRawOverrideResponseToFrontend = (
    response: BackendSaveOverrideResponse,
    _meta: FetchBaseQueryMeta | undefined,
    arg: SaveCollectionOverrideArgs,
): SaveCollectionOverrideResponse => {
    const { proscopeId } = arg;

    return {
        message: response.message,
        proscopeId: response.proscopeId,
        author: response.author,
        savedData: mapToFrontendSchema(
            response.savedData,
            proscopeId,
            arg.collectionId,
        ),
    };
};

export const transformSaveOverrideResponse = createDataMapper<
    BackendSaveOverrideResponse,
    SaveCollectionOverrideResponse,
    SaveCollectionOverrideArgs
>(mapRawOverrideResponseToFrontend, false);

const mapRawHistoryResponseToFrontend = (
    response: GetOverrideHistoryResponse,
    _meta: FetchBaseQueryMeta | undefined,
    arg: GetOverrideHistoryArgs,
): GetOverrideHistoryResponse => {
    return {
        proscopeId: response.proscopeId || arg.proscopeId,
        totalEdits: response.totalEdits || 0,

        history: (response.history || []).sort(
            (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime(),
        ),
    };
};

export const transformOverrideHistoryResponse = createDataMapper<
    GetOverrideHistoryResponse,
    GetOverrideHistoryResponse,
    GetOverrideHistoryArgs
>(mapRawHistoryResponseToFrontend, false);
